import { useTranslator } from '@Localization';
import { countries } from 'countries-list';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { Select } from '../../../../design/inputs/select/Select';
import { S_FieldLabel } from '../../../../design/labels/fieldLabel/S_FIeldLabel';
import { S_Link } from '../../../../design/links/S_Link';
import { FormAlert } from '../../../components/FormAlert';
import { ValidatedTextInput } from '../../../components/ValidatedTextInput';
import { InputStatus } from '../../../models/error.model';
import { Auth } from '../../../routes/config/Auth';
import { Regulations } from '../../../routes/config/ExternalLinks';
import { PasswordWithScoreInputsContainer } from '../containers/PasswordWithScoreInputsContainer';

interface IRegistrationFormProps {
  emailStatus: InputStatus;
  passwordStatus: InputStatus;
  externalStatus: InputStatus;
  isDisabledSubmit: boolean;
  email: string;
  company: string;
  country: string;
  onRegister: () => Promise<void>;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onPasswordStatusChange: (status: InputStatus) => void;
  validateEmail: (value: string) => void;
  onCompanyChange: (value: string) => void;
  onCountryChange: (value: string) => void;
}

export const RegistrationForm = observer((props: IRegistrationFormProps): ReactElement => {
  const { t } = useTranslator();
  const location = useLocation();

  const validationErrorOccurred = props.externalStatus.isValid === false || props.emailStatus.isValid === false || props.passwordStatus.isValid === false;

  const countryOptions = Object.entries(countries).map(([key, value]) => ({ value: key, label: value.name }));

  return (
    <S_Card>
      <div className='auth-form'>
        <FormAlert
          errors={props.externalStatus}
          useDefaultMessageWhenCodeNotFound={true}
        />
        <div className='form-heder'>{t('register')}</div>
        <ValidatedTextInput
          isRequired
          id='register-email-input'
          value={props.email}
          labelText={t('email_address')}
          type={'text'}
          placeholder={t('enter_business_email')}
          onChange={props.onEmailChange}
          onBlur={props.validateEmail}
          isValid={props.emailStatus.isValid}
          feedbacks={props.emailStatus.errorCodes}
        />
        <PasswordWithScoreInputsContainer
          labelText={t('password')}
          onPasswordChange={props.onPasswordChange}
          onPasswordStatusChange={props.onPasswordStatusChange}
        />
        <ValidatedTextInput
          id='register-company-input'
          value={props.company}
          labelText={t('organisation')}
          type={'text'}
          placeholder={t('enter_organisation')}
          onChange={props.onCompanyChange}
        />
        <div>
          <S_FieldLabel text={t('country')} />
          <Select
            placeholder={t('enter_country')}
            value={countryOptions.find((o) => o.value === props.country)}
            onChange={(_, v) => {
              props.onCountryChange(v.value ?? '');
            }}
            options={countryOptions}
            openOnFocus={false}
          />
        </div>
        <ButtonWithLoader
          id='create-account-button'
          loaderKey='create-account-button'
          color='primary'
          disabled={validationErrorOccurred || props.isDisabledSubmit}
          loading={props.isDisabledSubmit}
          onClick={props.onRegister}
        >
          {t('create_account')}
        </ButtonWithLoader>

        <div className='auth-link'>
          {t('already_have_an_account')}
          <S_Link
            color='anchor'
            to={`${Auth.Login.Path}/${location.search}`}
          >
            {t('log_in_here')}
          </S_Link>
        </div>
        <div className='admin-info'>
          <div>{t('admin_info')}</div>
          <div>
            <span>
              {t('more_info')}{' '}
              <a
                href={Regulations.PrivacyPolicy.Path}
                target='_blank'
                rel='noreferrer'
              >
                {t('privacy_policy')}
              </a>
            </span>
          </div>
        </div>
      </div>
    </S_Card>
  );
});
